// ============================================================================
// FSA PARTNER MASTER HOOK (NCP-13)
// ============================================================================
// Mirrors useFsaSignature's master hooks (data / loading / error / pagination
// + refetch). Per-client partner roster for partnership-firm engagements.

import { useCallback, useEffect, useState } from 'react';
import { fsaPartnerService } from 'api/services/fsa/fsaPartnerService';
import type { Partner, PartnerSearchRequest } from 'api/types/fsa/partner.types';

interface Pagination {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

const EMPTY_PAGINATION: Pagination = {
  currentPage: 1, pageSize: 20, totalPages: 0, totalItems: 0, hasNextPage: false, hasPreviousPage: false,
};

export function useFsaPartners(params?: PartnerSearchRequest) {
  const [data, setData] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState<Pagination>(EMPTY_PAGINATION);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fsaPartnerService.getAll(params);
      setData(resp.data);
      setPagination(resp.pagination as Pagination);
      setError(null);
    } catch (err) {
      setError(err as Error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, pagination, refetch: fetch };
}
